// @ts-check

/**
 * CUSTOM: Fælles variant-synkronisering for custom vælgere på produktsiden (kæde, antal navne, …).
 *
 * Problem det løser: hver custom vælger kendte kun "sin" valgmulighed og nulstillede de andre
 * (fx valgt 5 navne → kurven fik "2 Navne"). Nu melder hver vælger sin værdi på sit rod-element:
 *   data-variant-option-index="1"   (0 = option1, 1 = option2, …)
 *   data-variant-option-value="Box Chain"
 * og sync() finder den ENE variant, der matcher alle valg (custom vælgere + Motions dropdowns),
 * sætter den i formularen og opdaterer prisen.
 *
 * Bruges af: assets/custom-chain-selector.js, snippets/name-selector.liquid
 */
(function () {
  /** @type {any} */
  const win = window;
  if (win.CustomVariantSync) return;

  /**
   * @typedef {{ id: number, available: boolean, price: number, compare_at_price: number | null, options: string[] }} Variant
   */

  /**
   * Ren logik (ingen DOM): find varianten der matcher alle valgte værdier.
   * @param {Variant[]} variants
   * @param {(string | null)[]} selected Valgt værdi pr. option-index (null = ukendt)
   * @returns {Variant | null}
   */
  function findVariant(variants, selected) {
    return (
      variants.find((v) => v.options.every((value, i) => selected[i] == null || selected[i] === value)) || null
    );
  }

  /** @returns {Variant[]} */
  function getVariants() {
    const el = document.querySelector('[data-variant-json]');
    try {
      return el ? JSON.parse(el.textContent || '[]') : [];
    } catch (e) {
      return [];
    }
  }

  /** @returns {HTMLSelectElement | null} */
  function getProductSelect() {
    return document.querySelector('[data-product-select]');
  }

  /** @returns {Variant | null} */
  function getCurrentVariant() {
    const select = getProductSelect();
    return getVariants().find((v) => String(v.id) === String(select && select.value)) || null;
  }

  /**
   * Aktuelle valg pr. option: custom vælger → Motions dropdown → nuværende variant.
   * @param {Record<number, string>} [override] Værdier der skal bruges i stedet (fx en kæde man overvejer)
   * @returns {(string | null)[]}
   */
  function getSelectedOptions(override) {
    const current = getCurrentVariant();
    const count = (getVariants()[0] || { options: [] }).options.length;
    /** @type {(string | null)[]} */
    const selected = [];
    for (let i = 0; i < count; i++) {
      if (override && override[i] != null) {
        selected.push(override[i]);
        continue;
      }
      /** @type {HTMLElement | null} */
      const custom = document.querySelector('[data-variant-option-index="' + i + '"]');
      if (custom && custom.dataset.variantOptionValue) {
        selected.push(custom.dataset.variantOptionValue);
        continue;
      }
      /** @type {HTMLSelectElement | null} */
      const motionSelect = document.querySelector(
        '[data-index="option' + (i + 1) + '"] select, select[data-index="option' + (i + 1) + '"]'
      );
      if (motionSelect && motionSelect.value) {
        selected.push(motionSelect.value);
        continue;
      }
      selected.push(current ? current.options[i] : null);
    }
    return selected;
  }

  /** @param {number} cents */
  function formatMoney(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' kr.';
  }

  /** @param {Variant} variant */
  function updatePrice(variant) {
    const priceEl = document.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.innerHTML = formatMoney(variant.price);
      priceEl.classList.toggle('sale-price', !!(variant.compare_at_price && variant.compare_at_price > variant.price));
    }
    const compareEl = document.querySelector('[data-compare-price]');
    const compareWrap = document.querySelector('[data-product-price-wrap]');
    if (compareEl && compareWrap) {
      const onSale = !!(variant.compare_at_price && variant.compare_at_price > variant.price);
      compareEl.innerHTML = onSale && variant.compare_at_price ? formatMoney(variant.compare_at_price) : '';
      compareWrap.classList.toggle('hide', !onSale);
    }
  }

  /**
   * Sæt varianten i formularen + opdater pris og URL.
   * @param {Variant} variant
   */
  function apply(variant) {
    const select = getProductSelect();
    if (select) select.value = String(variant.id);
    updatePrice(variant);
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Find varianten for de aktuelle valg og sæt den. Returnerer null hvis kombinationen ikke findes/er udsolgt.
   * @returns {Variant | null}
   */
  function sync() {
    const variant = findVariant(getVariants(), getSelectedOptions());
    if (variant && variant.available) {
      apply(variant);
      return variant;
    }
    return null;
  }

  win.CustomVariantSync = { findVariant, getVariants, getSelectedOptions, updatePrice, apply, sync };
})();
